import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { axiosInstance } from "../../../axios/axiosInstance";

const SCOPE_TEXT = {
  "cart:write": "Add products to your cart",
  "cart:read": "See what is in your cart",
  "catalog:read": "Look up products and prices",
};

export const OAuthConsent = () => {
  const [params] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const clientId = params.get("client_id") || "";
  const redirectUri = params.get("redirect_uri") || "";
  const responseType = params.get("response_type") || "";
  const codeChallenge = params.get("code_challenge") || "";
  const codeChallengeMethod = params.get("code_challenge_method") || "";
  const scope = params.get("scope") || "";
  const state = params.get("state") || "";

  useEffect(() => {
    axiosInstance
      .get("mcp/oauth/authorize-info", {
        params: {
          client_id: clientId,
          redirect_uri: redirectUri,
          response_type: responseType,
          code_challenge: codeChallenge,
          code_challenge_method: codeChallengeMethod,
          scope: scope,
        },
      })
      .then((response) => {
        setInfo(response?.data?.data || null);
      })
      .catch((err) => {
        setError(err?.response?.data?.message || "This request could not be verified.");
      })
      .finally(() => setLoading(false));
  }, [clientId, redirectUri, responseType, codeChallenge, codeChallengeMethod, scope]);

  const send = (path) => {
    if (submitting) return;
    setSubmitting(true);

    axiosInstance
      .post(path, {
        clientId,
        redirectUri,
        codeChallenge,
        codeChallengeMethod,
        scope,
        state,
      })
      .then((response) => {
        const target = response?.data?.data?.redirectTo;
        if (target) {
          window.location.href = target;
          return;
        }
        setError("The response from the server was not understood.");
        setSubmitting(false);
      })
      .catch((err) => {
        setError(err?.response?.data?.message || "That could not be completed.");
        setSubmitting(false);
      });
  };

  return (
    <section className="myAccount-section">
      <div className="container-fluid container-lg">
        <div className="row justify-content-center">
          <div className="col-md-9 col-lg-7">
            <div className="acc-header py-3">
              <div className="myaccount-address myAccount-header">
                <h1 className="myaccount-title">Connect an application</h1>
              </div>
            </div>

            {loading && <p className="fs-15 mt-4">Checking this request&hellip;</p>}

            {!loading && error && (
              <div className="alert alert-danger mt-4" role="alert">
                <h3 className="fs-16 fw-bold mb-2">This request was not approved</h3>
                <p className="fs-14 mb-0">{error}</p>
                <p className="fs-14 mb-0 mt-2">
                  Nothing has been shared. You can close this window.
                </p>
              </div>
            )}

            {!loading && !error && info && (
              <>
                <div className="mt-4">
                  <p className="fs-16 mb-0">
                    <strong>{info.clientName}</strong> wants to act on your WES account.
                  </p>
                  <p className="fs-15 text-muted mt-2 mb-0">
                    Only approve this if you started it yourself, just now.
                  </p>
                </div>

                <div className="mt-4">
                  <h3 className="website-settings-title">It will be able to</h3>
                  <ul className="fs-15 mt-2">
                    {(info.scopes || []).map((s) => (
                      <li key={s} className="mb-1">
                        {SCOPE_TEXT[s] || s}
                      </li>
                    ))}
                  </ul>
                  <p className="fs-14 text-muted mt-3 mb-0">
                    It will never see your password, and it cannot place an order or pay for anything.
                    You can disconnect it at any time from My Account &rarr; AI Access.
                  </p>
                </div>

                <div className="d-flex flex-wrap gap-2 mt-4">
                  <button
                    type="button"
                    className="btn btn-products theme-primary"
                    disabled={submitting}
                    onClick={() => send("mcp/oauth/approve")}
                  >
                    {submitting ? "Please wait..." : "Approve"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-products btn-unavailable"
                    disabled={submitting}
                    onClick={() => send("mcp/oauth/deny")}
                  >
                    Cancel
                  </button>
                </div>

                <p className="fs-13 text-muted mt-4 mb-0" style={{ overflowWrap: "anywhere" }}>
                  You will be returned to {info.redirectUri}
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
